import * as React from 'react'

import { Skeleton } from '#/components/ui/skeleton'
import { StatusDot } from '#/components/ui/status-dot'
import type { StatusTone } from '#/lib/status-styles'
import { cn } from '#/lib/utils'

// The one table (DESIGN.md §10 "Tables"): editorial rather than a spreadsheet.
// Linen hairlines between rows and nothing else — no zebra, no cell grid, no
// header band. The header is a quiet sentence-case caption that sticks while
// the table scrolls; rows are comfortable and take a faint sand tint on hover.
// A table never boxes itself: on a page it sits in a `TableFrame` (the card
// is the frame), inside another surface it is flush with that surface's
// padding. Too wide for its column, it scrolls sideways inside its container,
// never the page.
//
// The model is `border-separate` with the rules drawn on the cells, not the
// rows: a collapsed border would scroll away from under a sticky header.

function Table({
  className,
  containerClassName,
  size = 'default',
  ...props
}: React.ComponentProps<'table'> & {
  // Bound the container's height (`max-h-*`) to make the body scroll under a
  // sticky header.
  containerClassName?: string
  // `dense` tightens a record list's rows (43px); `sm` tightens them further
  // for reference tables in narrow panes.
  size?: 'default' | 'dense' | 'sm'
}) {
  return (
    <div
      data-slot="table-container"
      className={cn(
        'relative w-full overflow-x-auto scrollbar-subtle',
        containerClassName,
      )}
    >
      <table
        data-slot="table"
        data-size={size}
        className={cn(
          'group/table w-full caption-bottom border-separate border-spacing-0 text-body-md',
          className,
        )}
        {...props}
      />
    </div>
  )
}

// A page-level table's surface: white paper on the bone canvas with a linen
// edge. Cells inside it inset their outer edges to the card's padding.
function TableFrame({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="table-frame"
      className={cn(
        'overflow-hidden rounded-xl border border-border bg-card shadow-elevated [--table-inset:1.25rem]',
        className,
      )}
      {...props}
    />
  )
}

// Header cells: a caption in stone over a linen rule, pinned to the top of a
// scrolling container. Declared here rather than on `TableHead` so a row header
// (`<TableHead scope="row">` in the body) reads as a cell, not a caption.
function TableHeader({ className, ...props }: React.ComponentProps<'thead'>) {
  return (
    <thead
      data-slot="table-header"
      className={cn(
        '[&_th]:sticky [&_th]:top-0 [&_th]:z-20 [&_th]:h-10 [&_th]:border-b [&_th]:border-border [&_th]:bg-card [&_th]:text-caption-md [&_th]:font-normal [&_th]:text-muted-foreground [&_tr]:hover:bg-transparent',
        className,
      )}
      {...props}
    />
  )
}

// The last row's rule is dropped: the frame's edge, or the white space under a
// flush table, closes it.
function TableBody({ className, ...props }: React.ComponentProps<'tbody'>) {
  return (
    <tbody
      data-slot="table-body"
      className={cn(
        '[&_th]:border-b [&_th]:border-border [&_th]:py-3 [&_th]:font-normal [&_th]:text-foreground group-data-[size=dense]/table:[&_th]:py-2.5 group-data-[size=sm]/table:[&_th]:py-2 [&>tr:last-child>*]:border-b-0',
        className,
      )}
      {...props}
    />
  )
}

function TableFooter({ className, ...props }: React.ComponentProps<'tfoot'>) {
  return (
    <tfoot
      data-slot="table-footer"
      className={cn(
        'text-foreground [&_td]:border-t [&_td]:border-b-0 [&_td]:border-border',
        className,
      )}
      {...props}
    />
  )
}

// `relative` anchors a whole-row link (`tableRowLinkClass`); `group/row` lets a
// row action wait for the row's hover or focus (`group-hover/row:`).
function TableRow({ className, ...props }: React.ComponentProps<'tr'>) {
  return (
    <tr
      data-slot="table-row"
      className={cn(
        'group/row relative transition-colors hover:bg-muted/40 has-aria-expanded:bg-muted/40 data-[state=selected]:bg-muted/60',
        className,
      )}
      {...props}
    />
  )
}

// Outer cells inset to the frame's padding when the table sits in one (the
// frame sets `--table-inset`), and stay flush with the surrounding text when it
// doesn't.
const EDGE_INSET =
  'first:pl-[var(--table-inset,0px)] last:pr-[var(--table-inset,0px)]'

function TableHead({
  className,
  numeric = false,
  ...props
}: React.ComponentProps<'th'> & { numeric?: boolean }) {
  return (
    <th
      data-slot="table-head"
      className={cn(
        'px-3 text-left align-middle whitespace-nowrap [&:has([role=checkbox])]:pr-0',
        EDGE_INSET,
        numeric && 'text-right',
        className,
      )}
      {...props}
    />
  )
}

// A body cell. The primary column (what the row *is*) reads in ink; pass
// `muted` for the secondary columns around it. `numeric` right-aligns figures
// in tabular digits so a column of them lines up.
function TableCell({
  className,
  numeric = false,
  muted = false,
  ...props
}: React.ComponentProps<'td'> & { numeric?: boolean; muted?: boolean }) {
  return (
    <td
      data-slot="table-cell"
      className={cn(
        'border-b border-border px-3 py-3 align-middle whitespace-nowrap group-data-[size=dense]/table:py-2.5 group-data-[size=sm]/table:py-2 [&:has([role=checkbox])]:pr-0',
        EDGE_INSET,
        muted ? 'text-muted-foreground' : 'text-foreground',
        numeric && 'text-right tabular-nums',
        className,
      )}
      {...props}
    />
  )
}

function TableCaption({
  className,
  ...props
}: React.ComponentProps<'caption'>) {
  return (
    <caption
      data-slot="table-caption"
      className={cn('mt-4 text-caption-md text-muted-foreground', className)}
      {...props}
    />
  )
}

// Text that keeps to one line and ellipses, with the whole of it on hover.
// Truncation needs a bounded column — a `table-fixed` table, or a `max-w-*`.
function TableText({
  children,
  title,
  className,
}: {
  children: React.ReactNode
  title?: string
  className?: string
}) {
  return (
    <span
      data-slot="table-text"
      title={title ?? (typeof children === 'string' ? children : undefined)}
      className={cn('block truncate', className)}
    >
      {children}
    </span>
  )
}

// A status in a table: a plain word, never a pill. A settled state (`done`)
// takes a plum mark; one that needs a person (`attention`, `failed`) an ember
// mark, with its word lifted to ink. Under way, new, idle and AI work are the
// word alone.
const TABLE_STATUS_MARK: Partial<Record<StatusTone, StatusTone>> = {
  done: 'done',
  attention: 'attention',
  failed: 'attention',
}

function TableStatus({
  tone,
  children,
  className,
  ...props
}: React.ComponentProps<'span'> & { tone?: StatusTone }) {
  const mark = tone ? TABLE_STATUS_MARK[tone] : undefined
  return (
    <span
      {...props}
      data-slot="table-status"
      data-tone={tone}
      className={cn(
        'whitespace-nowrap',
        mark === 'attention' ? 'text-foreground' : 'text-muted-foreground',
        className,
      )}
    >
      {/* An inline mark on the text's middle rather than a flex row, so the
          word keeps the baseline of the cells beside it. */}
      {mark ? (
        <StatusDot tone={mark} className="mr-2 inline-block align-middle" />
      ) : null}
      {children}
    </span>
  )
}

// One warm line where the rows would be — never a boxed illustration.
function TableEmpty({
  colSpan,
  children,
}: {
  colSpan: number
  children: React.ReactNode
}) {
  return (
    <tr data-slot="table-empty">
      <td
        colSpan={colSpan}
        className="px-5 py-12 text-center text-body-md whitespace-normal text-muted-foreground"
      >
        {children}
      </td>
    </tr>
  )
}

// Placeholder rows in the table's own rhythm, so the frame doesn't jump when
// the data lands. Bar widths vary per column so it reads as text, not a grid.
const SKELETON_WIDTHS = ['w-3/5', 'w-2/5', 'w-1/2', 'w-1/3', 'w-2/3']

function TableSkeletonRows({
  columns,
  rows = 5,
}: {
  columns: number
  rows?: number
}) {
  return Array.from({ length: rows }, (_, r) => (
    <tr key={r} data-slot="table-skeleton-row" aria-hidden="true">
      {Array.from({ length: columns }, (_, c) => (
        <TableCell key={c}>
          <Skeleton
            className={cn(
              'h-3.5',
              SKELETON_WIDTHS[(r + c * 2) % SKELETON_WIDTHS.length],
            )}
          />
        </TableCell>
      ))}
    </tr>
  ))
}

// Makes a row open something: put it on the one link in the row's primary
// cell, and its hit area (and pointer) stretches over the whole row, the focus
// ring drawn inset round the row (the `focus-ring-inset` treatment) rather
// than round the link text. Any other control in the row needs `relative z-10`
// to stay clickable above it.
// A secondary link inside a cell (a workspace, a related record): muted like
// its column, ink and underlined on hover.
const tableLinkClass =
  'rounded-sm underline-offset-2 hover:text-foreground hover:underline focus-ring'

// A default-size row: `py-3` around a 22px `body-md` line, plus its hairline.
// For sizing a page of rows to the viewport.
const TABLE_ROW_PX = 47
// A dense table's row (the record tables: the Vault, a workspace's documents).
const TABLE_DENSE_ROW_PX = 43

// A row action (`group/row`) that waits for its row's hover or focus from
// `md`, and is always there on touch, which has no hover.
const rowActionClass =
  'transition-opacity md:opacity-0 md:group-focus-within/row:opacity-100 md:group-hover/row:opacity-100 md:focus-visible:opacity-100 md:[@media(hover:none)]:opacity-100'

const tableRowLinkClass =
  "outline-none after:absolute after:inset-0 after:content-[''] focus-visible:after:outline-2 focus-visible:after:-outline-offset-2 focus-visible:after:outline-ring"

// The same table for rendered HTML (guidance, the scoring criteria), where the
// markup isn't ours to class: descendant rules on the prose wrapper.
// Width, wrapping and type size stay with each surface; the rules, the quiet
// header and the row tint are shared. `th:not([align])` so a GFM-aligned column
// keeps its alignment — `align` is a presentational attribute and loses to any
// author `text-align`. Mirrors `TableHeader` / `TableBody` / `TableCell` above:
// change one, change the other.
const PROSE_TABLE_CLASS = [
  '[&_table]:w-full [&_table]:border-separate [&_table]:border-spacing-0 [&_table]:tabular-nums',
  '[&_caption]:mb-2 [&_caption]:text-left [&_caption]:text-caption-md [&_caption]:text-muted-foreground',
  '[&_th]:border-b [&_th]:border-border [&_th]:px-3 [&_th]:pt-1 [&_th]:pb-2 [&_th]:align-bottom [&_th]:text-caption-md [&_th]:font-normal [&_th]:text-muted-foreground [&_th:first-child]:pl-0 [&_th:not([align])]:text-left',
  '[&_td]:border-b [&_td]:border-border [&_td]:px-3 [&_td]:py-2.5 [&_td]:align-top [&_td:first-child]:pl-0',
  '[&_tbody>tr:last-child>td]:border-b-0 [&_tbody_tr]:transition-colors [&_tbody_tr:hover]:bg-muted/40',
].join(' ')

export {
  PROSE_TABLE_CLASS,
  Table,
  TableFrame,
  TableHeader,
  TableBody,
  TableFooter,
  TableHead,
  TableRow,
  TableCell,
  TableCaption,
  TableText,
  TableStatus,
  TableEmpty,
  TableSkeletonRows,
  TABLE_ROW_PX,
  TABLE_DENSE_ROW_PX,
  rowActionClass,
  tableLinkClass,
  tableRowLinkClass,
}
